import React, { useContext, useState } from 'react'
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material'
import { LeadCard } from '@marketing-milk/interfaces'
import { leadCardContext } from './context/LeadCardContext'
import { leadCardDTOWire } from './lead-card.wire'

interface StartFromExistingFormProps {
  forms: LeadCard[]
}

// Forms are immutable, so a variant of an existing form can only be made by copying it into a
// new one. This sits at the top of the builder rather than gating it behind a "blank or copy?"
// screen. The declare step opens this picker for copying and skips it when starting blank.
export function StartFromExistingForm({ forms }: StartFromExistingFormProps) {
  const leadCard = useContext(leadCardContext)
  const [source, setSource] = useState<LeadCard | null>(null)
  const [pendingSource, setPendingSource] = useState<LeadCard>()

  const applySource = (form: LeadCard) => {
    // Meta accepts duplicate form names, but two identically named forms are indistinguishable
    // in every picker afterwards — and immutable, so unfixable. Prefilled rather than blanked so
    // the common "same form, small change" copy is one overwrite instead of a retype.
    leadCard.replaceLeadCard({ ...leadCardDTOWire(form), formName: `${form.formName} (copy)` })
    setSource(form)
  }

  const chooseSource = (form: LeadCard | null) => {
    if (!form) return
    if (leadCard.touched) {
      setPendingSource(form)
      return
    }
    applySource(form)
  }

  return (
    <div className="mb-4">
      <label
        htmlFor="lead-form-copy-source"
        className="block mb-2 text-sm font-semibold text-gray-700"
      >
        Copy from an existing form
      </label>
      <Autocomplete
        id="lead-form-copy-source"
        size="small"
        fullWidth
        options={forms}
        value={source}
        onChange={(_, selected) => chooseSource(selected)}
        getOptionLabel={form => form.formName}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            autoFocus
            placeholder="Search forms by name…"
            inputProps={{ ...params.inputProps, 'aria-describedby': 'lead-form-copy-hint' }}
          />
        )}
      />
      <p id="lead-form-copy-hint" className="text-xs text-gray-500 mt-2 mb-0">
        Copies questions and content. The original stays unchanged.
      </p>

      <Dialog open={!!pendingSource} onClose={() => setPendingSource(undefined)}>
        <DialogTitle>Replace what you've written?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Starting from "{pendingSource?.formName}" will overwrite the changes you've made to this
            form.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingSource(undefined)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (pendingSource) applySource(pendingSource)
              setPendingSource(undefined)
            }}
          >
            Replace
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
